import { getBookmarkTime, type Bookmark, type Library } from "./bookmarks";

export function mergeBookmarks(
  localBookmarks: Bookmark[],
  serverBookmarks: Bookmark[],
): Bookmark[] {
  const merged = new Map<string, Bookmark>();

  for (const bookmark of serverBookmarks) {
    merged.set(bookmark.readingId, bookmark);
  }

  for (const local of localBookmarks) {
    const existing = merged.get(local.readingId);

    if (!existing) {
      merged.set(local.readingId, local);
    } else if (local.currentPage > existing.currentPage) {
      merged.set(local.readingId, {
        ...local,
        stars: local.stars ?? existing.stars,
        completed: local.completed ?? existing.completed,
      });
    } else if (local.currentPage === existing.currentPage) {
      const localTime = getBookmarkTime(local);
      const existingTime = getBookmarkTime(existing);

      if (localTime > existingTime) {
        merged.set(local.readingId, {
          ...local,
          stars: local.stars ?? existing.stars,
          completed: local.completed ?? existing.completed,
        });
      }
      // A timestamp tie keeps the server's copy: it is authoritative.
    }
  }

  return Array.from(merged.values());
}

// Latest lastupdate wins every merge, so a client clock far ahead would pin
// its bookmark against every later write from a correct one. The server
// refuses anything further ahead of its own clock; clients stamp under it.
export const MAX_BOOKMARK_CLOCK_SKEW_MS = 5 * 60 * 1000;

/** The fields the latest-wins rule reads; the server's int64 form qualifies. */
interface Mergeable {
  lastupdate: Date | number;
  completed?: boolean | undefined;
  stars?: unknown;
}

/**
 * The server's rule for one reading (Q10): the incoming bookmark replaces the
 * stored one when its `lastupdate` is later, so a reader can move a bookmark
 * backward. Returns what the bookmark becomes, or `null` when the stored copy
 * stands (a tie keeps it). `stars` and `completed` fall back to the stored
 * value when the winner omits them.
 *
 * Not `mergeBookmarks`, which keeps the furthest page for the anonymous-to-
 * account merge. The client's optimistic state and convex-reader's
 * `library.upsertBookmarks` both use this one.
 */
export function mergeBookmarkLatestWins<B extends Mergeable>(
  stored: B | null,
  incoming: B,
): B | null {
  if (stored === null) return incoming;
  if (timeOf(incoming) <= timeOf(stored)) return null;
  return {
    ...incoming,
    stars: incoming.stars ?? stored.stars,
    completed: incoming.completed ?? stored.completed,
  };
}

const timeOf = (bookmark: Mergeable): number =>
  bookmark.lastupdate instanceof Date
    ? bookmark.lastupdate.getTime()
    : bookmark.lastupdate;

export function mergeLibraryIds(
  localIds: string[],
  serverIds: string[],
): string[] {
  return [...new Set([...serverIds, ...localIds])];
}

export function mergeLibraries(
  local: Library,
  server: Library,
  readerId: string,
): Library {
  return {
    readerId,
    bookmarks: mergeBookmarks(local.bookmarks, server.bookmarks),
    library: mergeLibraryIds(local.library, server.library),
    resumeReadings: server.resumeReadings ?? local.resumeReadings,
  };
}
